import {
  HS_POINT_CLOUD_MIN_SPACING_SHARE,
  HS_POINT_CLOUD_TARGET_SPACING_SHARE,
  hsPointCloudBudgetDevice,
  hsResolvePointCloudBudget,
  type HsFrustumObjectInfo,
} from '@sibe/hyperscene';
import type { HsPointCloudLimits } from './decoded-geometry-store.class';
import type { HsFrustumDownloadSettings } from './frustum-download-plan.function';

/** While navigating, point clouds are requested coarsely: at most this many points... */
export const HS_POINT_CLOUD_NAVIGATION_POINT_LIMIT: number = 500_000;
/** ...and, with the level LOD, at most this many levels above the coarsest. */
export const HS_POINT_CLOUD_NAVIGATION_LEVEL_SPAN: number = 2;
/** Blocks the budget LOD may return; the point budget limits it long before. */
export const HS_POINT_CLOUD_BUDGET_BOX_LIMIT: number = 100_000;

/** What each Frustum request asks for. */
export interface HsFrustumRequestSettings {
  meshLimit: number;
  /** Smallest apparent mesh size, in degrees. */
  meshAngle: number;
  meshSortOrder: 'distance' | 'angular-size';
  lineLimit: number;
  lineAngle: number;
  /**
   * `levels` refines by octree level within `minLevel`..`maxLevel`; `budget` refines nearest first up to a
   * point budget.
   */
  pointCloudLodStrategy: 'levels' | 'budget';
  pointCloudSortOrder: 'distance' | 'angular-size';
  /** Level LOD: most points and blocks. */
  pointCloudLimit: number;
  pointCloudBoxLimit: number;
  /** Level LOD: smallest apparent block size, in degrees. */
  pointCloudAngle: number;
  minLevel: number;
  maxLevel: number;
  /** Budget LOD: points to keep, or 0 for a budget that fits the device. */
  pointCloudBudget: number;
  /** Drawn point size in pixels; the budget LOD stops refining below about two thirds of it. */
  pointSize: number;
}

/** The demo viewer's defaults. */
export const HS_DEFAULT_FRUSTUM_REQUEST_SETTINGS: HsFrustumRequestSettings = {
  meshLimit: 150_000,
  meshAngle: 0.5,
  meshSortOrder: 'angular-size',
  lineLimit: 150_000,
  lineAngle: 1,
  pointCloudLodStrategy: 'levels',
  pointCloudSortOrder: 'angular-size',
  pointCloudLimit: 8_000_000,
  pointCloudBoxLimit: 2000,
  pointCloudAngle: 20,
  minLevel: 0,
  maxLevel: 10,
  pointCloudBudget: 0,
  pointSize: 2,
};

/** The drawing buffer the budget LOD sizes its automatic budget for. */
export interface HsFrustumViewport {
  drawingBufferWidth: number;
  drawingBufferHeight: number;
}

/** Points and blocks the viewer keeps decoded under `settings`. */
export function hsPointCloudLimits(
  settings: HsFrustumRequestSettings,
  viewport: HsFrustumViewport
): HsPointCloudLimits {
  if (settings.pointCloudLodStrategy !== 'budget') {
    return { points: settings.pointCloudLimit, geometries: settings.pointCloudBoxLimit };
  }
  return {
    points: hsResolvePointCloudBudget(settings.pointCloudBudget, { ...hsPointCloudBudgetDevice(), ...viewport }),
    geometries: HS_POINT_CLOUD_BUDGET_BOX_LIMIT,
  };
}

export interface HsFrustumObjectsOptions {
  /** Ask for coarse point clouds while the camera moves; the full detail follows when it stops. */
  navigating: boolean;
  /** Include meshes and lines; point-cloud-only refreshes leave them out. */
  includeStaticGeometry: boolean;
  viewport: HsFrustumViewport;
  /**
   * Points to request with the budget LOD instead of the limit that `settings` and `navigating` give, for
   * hosts that regulate downloads themselves (for example by the measured draw cost).
   */
  pointLimit?: number;
}

/** The per-kind entries of a Frustum request (`hsMakeFrustumRequest`'s `frustumObjects`). */
export function hsFrustumObjects(
  settings: HsFrustumRequestSettings,
  options: HsFrustumObjectsOptions
): HsFrustumObjectInfo[] {
  const { points, geometries }: HsPointCloudLimits = hsPointCloudLimits(settings, options.viewport);
  const { minLevel, maxLevel } = settings;
  const navigating: boolean = options.navigating;
  let pointClouds: Omit<HsFrustumObjectInfo, 'type'>;
  if (settings.pointCloudLodStrategy === 'budget') {
    // The server refines by screen spacing within the budget; level range and angle do not apply.
    pointClouds = {
      lodStrategy: 'budget',
      limit: options.pointLimit ?? (navigating ? Math.min(points, HS_POINT_CLOUD_NAVIGATION_POINT_LIMIT) : points),
      targetSpacingPixels: settings.pointSize * HS_POINT_CLOUD_TARGET_SPACING_SHARE,
      minSpacingPixels: settings.pointSize * HS_POINT_CLOUD_MIN_SPACING_SHARE,
      boxesLimit: geometries,
      angle: 0,
      minLevel,
      maxLevel,
    };
  } else if (navigating) {
    pointClouds = {
      limit: Math.min(points, HS_POINT_CLOUD_NAVIGATION_POINT_LIMIT),
      minLevel,
      maxLevel: Math.min(maxLevel, minLevel + HS_POINT_CLOUD_NAVIGATION_LEVEL_SPAN),
      angle: settings.pointCloudAngle,
      boxesLimit: geometries,
    };
  } else {
    pointClouds = { limit: points, minLevel, maxLevel, angle: settings.pointCloudAngle, boxesLimit: geometries };
  }
  return [
    ...(options.includeStaticGeometry
      ? [
          {
            type: 'mesh' as const,
            limit: settings.meshLimit,
            angle: settings.meshAngle,
            sortOrder: settings.meshSortOrder,
          },
          {
            type: 'line' as const,
            limit: settings.lineLimit,
            angle: settings.lineAngle,
            sortOrder: 'distance' as const,
          },
        ]
      : []),
    { type: 'pointCloud', ...pointClouds, sortOrder: settings.pointCloudSortOrder },
  ];
}

/**
 * Point cloud download batches for the budget LOD. It sends blocks nearest and sparsest first, so the first
 * parallel requests take four blocks each and later batches grow to 8 MiB: the nearest blocks arrive within
 * about a second and the full download stays as fast as fixed batches. The level LOD keeps fixed batches.
 */
export function hsPointCloudDownloadBatching(
  settings: Pick<HsFrustumRequestSettings, 'pointCloudLodStrategy'>
): Pick<HsFrustumDownloadSettings, 'pointCloudBatchBytes' | 'pointCloudFirstBatchSize'> {
  return settings.pointCloudLodStrategy === 'budget'
    ? { pointCloudFirstBatchSize: 4, pointCloudBatchBytes: 8 * 1024 * 1024 }
    : {};
}
